import { useState, useEffect, useRef, useCallback } from 'react'
import AgentStreamDemo from './AgentStreamDemo'


const SUGGESTIONS = [
  'Build a SaaS dashboard with charts and dark mode',
  'Create an e-commerce store with cart and checkout',
  'Make a portfolio site with animations',
  'Build a real-time chat app with typing indicators',
]

const SECTIONS = [
  { id: 'hero', letter: 'PULSE', anim: 'anim-assemble', paraAnim: 'para-slide-left', full: true },
  { id: 'features', letter: 'P', anim: 'anim-glitch', paraAnim: 'para-slide-right', navLabel: 'Features', icon: '⚡', color: '#c4b95e', title: 'Prompt to App', subtitle: 'Zero to production in seconds', desc: 'Describe any app in plain English. PULSE generates the complete full-stack application — React components, API routes, database schema, styling, and deployment config — all at once.', bullets: ['Full-stack code generation', 'Auto file structure', 'Live preview in browser', 'One-click deploy'], code: `// Just describe it:\n"Build a todo app with auth and dark mode"\n\n// PULSE generates:\n✓ React + TypeScript frontend\n✓ Express API with JWT auth\n✓ PostgreSQL schema\n✓ Tailwind dark mode\n✓ Docker + deploy config` },
  { id: 'voice', letter: 'U', anim: 'anim-morph', paraAnim: 'para-slide-left', navLabel: 'Voice', icon: '🎙', color: '#f5a157', title: 'Voice Coding', subtitle: 'Hands-free development', desc: 'Talk to your IDE. Powered by Whisper STT and Web Speech API, PULSE transcribes your voice in real-time and executes commands instantly.', bullets: ['Whisper STT transcription', 'Web Speech API fallback', 'Voice-to-code commands', 'Real-time audio visualization'], code: `// Say it out loud:\n🎙 "Create a new React component\n    called UserCard with props for\n    name, avatar, and role"\n\n// PULSE creates:\n// src/components/UserCard.tsx ✓\n// Adds to index exports ✓\n// Opens in editor ✓` },
  { id: 'collab', letter: 'L', anim: 'anim-shatter', paraAnim: 'para-fade-up', navLabel: 'Collab', icon: '🔴', color: '#f07070', title: 'Real-time Collab', subtitle: 'Code together, live', desc: 'Powered by Yjs CRDT and Socket.io, multiple developers can edit the same file simultaneously with live cursors, presence indicators, and conflict-free merging.', bullets: ['Yjs CRDT conflict resolution', 'Live cursor presence', 'Shared file tree & terminal', 'Voice + text chat'], code: `// alice.ts is editing line 5...\n// bob joins the session\n\nconst users = [\n  { id: 1, name: "Alice" },  // ← alice\n  { id: 2, name: "Bob" },    // ← bob\n]\n\n// Changes merge instantly\n// No conflicts. Ever. ✓` },
  { id: 'deploy', letter: 'S', anim: 'anim-wave', paraAnim: 'para-slide-right', navLabel: 'Deploy', icon: '🚀', color: '#5fd4a4', title: 'Instant Deploy', subtitle: 'Live URL in one click', desc: 'Deploy to production without leaving the IDE. PULSE handles CI/CD, environment variables, SSL certificates, and CDN configuration automatically.', bullets: ['One-click deploy', 'Auto SSL + CDN', 'Environment secrets vault', 'CI/CD pipeline built-in'], code: `$ pulse deploy\n\n✓ Building production bundle...\n✓ Running type checks...\n✓ Uploading to edge network...\n✓ SSL certificate issued...\n✓ DNS propagated...\n\n🚀 Live at: https://myapp.pulse.dev\n   Deploy time: 23s` },
  { id: 'pricing', letter: 'E', anim: 'anim-flicker', paraAnim: 'para-fade-up', navLabel: 'Pricing' },
]

export default function LandingPage({ onSubmit }: { onSubmit?: (prompt: string) => void }) {
  const [input, setInput] = useState('')
  const [activeSuggestion, setActiveSuggestion] = useState(0)
  const [inputFocused, setInputFocused] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const sectionRefs = useRef<(HTMLElement | null)[]>([])

  useEffect(() => {
    const timer = setInterval(() => setActiveSuggestion(s => (s + 1) % SUGGESTIONS.length), 3000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const container = scrollRef.current
    if (!container) return
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = sectionRefs.current.indexOf(entry.target as HTMLElement)
            if (idx !== -1) setActiveIndex(idx)
          }
        })
      },
      { threshold: 0.5, root: container }
    )
    sectionRefs.current.forEach((ref) => { if (ref) observer.observe(ref) })
    return () => observer.disconnect()
  }, [])

  const scrollToSection = useCallback((idx: number) => {
    sectionRefs.current[idx]?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const scrollToId = useCallback((id: string) => {
    const idx = SECTIONS.findIndex(s => s.id === id)
    if (idx !== -1) scrollToSection(idx)
  }, [scrollToSection])

  const handleSubmit = () => { if (input.trim()) { if (onSubmit) onSubmit(input.trim()) } }
  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit() } }

  const onPointerMove = (e: React.PointerEvent) => {
    const el = scrollRef.current
    if (!el) return
    el.style.setProperty('--mx', `${e.clientX}px`)
    el.style.setProperty('--my', `${e.clientY}px`)
  }

  return (
    <div className="app-bg">
      {/* Background gradient */}
      <div className="app-bg-gradient">
        <div className="app-bg-gradient-top" />
        <div className="app-bg-gradient-bottom" />
      </div>

      {/* Nav */}
      <nav className="top-nav">
        <div className="nav-logo">
          <div className="nav-logo-mark">P</div>
          <span className="nav-logo-text">PULSE</span>
          <span className="nav-badge">BETA</span>
        </div>
        <div className="nav-links">
          {SECTIONS.filter(s => s.navLabel).map(s => (
            <button key={s.id} className="nav-link" onClick={() => scrollToId(s.id)}>{s.navLabel}</button>
          ))}
        </div>
        <button className="nav-signin" onClick={() => onSubmit?.('__login__')}>Sign In</button>
      </nav>

      {/* Dot nav */}
      <div className="dot-nav">
        {SECTIONS.map((_, i) => (
          <button key={i} className={`dot ${activeIndex === i ? 'dot-active' : ''}`} onClick={() => scrollToSection(i)} />
        ))}
      </div>

      {/* Scroll container */}
      <div className="scroll-container" ref={scrollRef} onPointerMove={onPointerMove}>
        {/* Cursor spotlight */}
        <div className="cursor-spot" />

        {/* HERO */}
        <section className="scroll-section" ref={el => { sectionRefs.current[0] = el }}>
          {/* PULSE watermark - same style as LandingPage */}
          <div className="pulse-watermark">
            <svg viewBox="0 0 900 200" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
              <defs>
                <linearGradient id="pulseGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#f79708" />
                  <stop offset="40%" stopColor="#ce2432" />
                  <stop offset="70%" stopColor="#5fd4a4" />
                  <stop offset="100%" stopColor="#f5a157" />
                </linearGradient>
              </defs>
              <text x="450" y="140" textAnchor="middle" dominantBaseline="middle"
                fontFamily="ui-sans-serif, system-ui, sans-serif" fontWeight="900" fontSize="170"
                letterSpacing="6" fill="none" stroke="rgba(164, 245, 110, 0.07)" strokeWidth="1">PULSE</text>
              <text className="pulse-draw-light" x="450" y="140" textAnchor="middle" dominantBaseline="middle"
                fontFamily="ui-sans-serif, system-ui, sans-serif" fontWeight="900" fontSize="170"
                letterSpacing="6" fill="none" stroke="url(#pulseGrad)" strokeWidth="1.6">PULSE</text>
            </svg>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: 30, alignItems: 'center', width: '100%', margin: '0 auto', paddingLeft: 100, paddingRight: 100, position: 'relative', zIndex: 2 }}>
            <div style={{ flex: 3, minWidth: 0 }}>
              <div className="hero-content" style={{ textAlign: 'left', margin: 0, maxWidth: 'none' }}>
                <div className="hero-badge">
                  <span className="hero-badge-dot" />
                  <span>The AI IDE that beats all others</span>
                </div>
                <h1 className="hero-title">
                  <span className="hero-line-1">Describe it.</span>
                  <span className="hero-line-2">PULSE builds it.</span>
                </h1>
                <p className="hero-sub">Full-stack apps from a single prompt. Voice-native. Real-time collab. Runs in browser, desktop, and VS Code.</p>
                <div className="hero-input-wrap">
                  <div className="hero-input-glow">
                  <div className={`hero-input-box ${inputFocused ? 'focused' : ''}`}>
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onFocus={() => setInputFocused(true)}
                      onBlur={() => setInputFocused(false)}
                      placeholder="What do you want to build?"
                      rows={2}
                    />
                    <div className="hero-input-footer">
                      <span className="hero-input-hint">ENTER TO BUILD</span>
                      <button className="hero-input-btn" onClick={handleSubmit} disabled={!input.trim()}>Build</button>
                    </div>
                  </div>
                  </div>
                  <div className="hero-suggestions">
                    {SUGGESTIONS.map((s, i) => (
                      <button key={s} className={`hero-suggestion ${activeSuggestion === i ? 'active' : ''}`} onClick={() => { setInput(s); inputRef.current?.focus() }}>
                        {s.length > 40 ? s.slice(0, 40) + '...' : s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0, height: 550, textAlign: 'left', marginTop:350 }}>
              <AgentStreamDemo />
            </div>
          </div>

          <div className="scroll-cue" style={{ bottom:-94 }}>
            <span>SCROLL TO EXPLORE</span>
            <div className="scroll-line" />
          </div>
        </section>

        {/* FEATURE SECTIONS */}
        {SECTIONS.slice(1).map((section, i) => {
          const idx = i + 1
          const isVisible = activeIndex === idx
          return (
            <section key={section.id} className="scroll-section feature-section" ref={el => { sectionRefs.current[idx] = el }}>
              {/* Background letter */}
              <div className="letter-wrapper section-bg-letter">
                <div className={`section-letter ${section.full ? 'full-word' : ''} ${isVisible ? `${section.anim} visible` : ''}`}>{section.letter}</div>
                <div className={`section-letter-glow ${section.full ? 'full-word' : ''} ${isVisible ? `${section.anim} visible` : ''}`}>{section.letter}</div>
              </div>

              {/* Feature content */}
              {section.title ? (
                <div className={`feature-content ${isVisible ? 'visible' : ''}`}>
                  <div className="feature-text">
                    <div className="feature-badge" style={{ borderColor: `${section.color}55`, background: `${section.color}12` }}>
                      <span>{section.icon}</span>
                      <span style={{ color: section.color }}>{section.subtitle?.toUpperCase()}</span>
                    </div>
                    <h2 className="feature-title">{section.title}</h2>
                    <p className="feature-desc">{section.desc}</p>
                    <div className="feature-bullets">
                      {section.bullets?.map((b, bi) => (
                        <div key={b} className="feature-bullet" style={{ transitionDelay: `${300 + bi * 90}ms` }}>
                          <div className="bullet-dot" style={{ background: `${section.color}22`, borderColor: `${section.color}55` }}>
                            <div style={{ background: section.color, boxShadow: `0 0 8px ${section.color}` }} />
                          </div>
                          <span>{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="feature-code-panel" style={{ borderColor: `${section.color}33` }}>
                    <div className="code-panel-header" style={{ borderBottomColor: `${section.color}22` }}>
                      <div className="code-dot red" /><div className="code-dot yellow" /><div className="code-dot green" />
                      <span className="code-filename">{section.title?.toLowerCase().replace(/\s+/g, '-')}.ts</span>
                      <span className="code-live" style={{ color: section.color, borderColor: `${section.color}55`, background: `${section.color}18` }}>LIVE</span>
                    </div>
                    <pre className="code-content" style={{ color: section.color }}>{section.code}</pre>
                  </div>
                </div>
              ) : (
                /* Pricing section */
                <div className={`pricing-content ${isVisible ? 'visible' : ''}`}>
                  <h2 className="pricing-title">Simple, transparent pricing</h2>
                  <div className="pricing-grid">
                    {[
                      { name: 'Free', price: '$0', period: '/forever', features: ['5 AI requests/day', '1 project', 'Community support', 'Browser IDE'], popular: false },
                      { name: 'Pro', price: '$19', period: '/mo', features: ['Unlimited AI requests', '10 projects', 'Voice coding', 'Priority support', 'All AI models'], popular: true },
                      { name: 'Enterprise', price: '$99', period: '/mo', features: ['Everything in Pro', 'Unlimited projects', 'SSO + audit logs', 'Team workspaces', 'Custom domain', 'SLA guarantee'], popular: false },
                    ].map(plan => (
                      <div key={plan.name} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                        {plan.popular && <div className="popular-badge">POPULAR</div>}
                        <div className="plan-name">{plan.name}</div>
                        <div className="plan-price">{plan.price}<span>{plan.period}</span></div>
                        <div className="plan-features">
                          {plan.features.map(f => (<div key={f} className="plan-feature"><span className="check">✓</span>{f}</div>))}
                        </div>
                        <button className="plan-btn" onClick={() => onSubmit?.('__login__')}>Get Started</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Description paragraph */}
              <span className={`section-description ${isVisible ? section.paraAnim + ' visible' : ''}`}>
                {section.id === 'features' && 'Prompt — One sentence is all it takes. Describe your vision and watch PULSE generate your entire app.'}
                {section.id === 'voice' && 'Unleash — Speak your code into existence. Hands-free development powered by real-time voice AI.'}
                {section.id === 'collab' && 'Link — Code together in real-time. Live cursors, shared terminals, zero merge conflicts.'}
                {section.id === 'deploy' && 'Ship — From localhost to live URL in seconds. One click, zero config, instant production.'}
                {section.id === 'pricing' && 'Evolve — Scale your workflow as you grow. Pick the plan that matches your ambition.'}
              </span>

              {section.id === 'pricing' && (
                <div className="scroll-cue">
                  <span>SCROLL TO EXPLORE</span>
                  <div className="scroll-line" />
                </div>
              )}
            </section>
          )
        })}

        {/* ── ALL FEATURES GRID ── */}
        <section className="flow-section">
          <div className="flow-inner">
            <h2 className="flow-heading">And <span className="gradient-text">30+ more</span> features</h2>
            <p className="flow-subtext">Everything you need to build, ship, and scale</p>
            <div className="features-grid">
              {[
                { icon: '🐛', title: 'AI Debugger', desc: 'Set breakpoints, inspect state, and ask AI to explain any bug' },
                { icon: '🔒', title: 'Security Scanner', desc: 'Automatic vulnerability detection across your entire codebase' },
                { icon: '📊', title: 'Analytics Dashboard', desc: 'Real-time usage metrics, error tracking, and performance insights' },
                { icon: '🗄️', title: 'Database Panel', desc: 'Visual schema editor, query runner, and migration manager' },
                { icon: '📦', title: 'Package Manager', desc: 'Search, install, and audit npm packages without leaving the IDE' },
                { icon: '🔑', title: 'Secrets Vault', desc: 'Encrypted environment variables with team-level access control' },
                { icon: '📝', title: 'Doc Generator', desc: 'AI-powered documentation from your code comments and types' },
                { icon: '🔄', title: 'Version History', desc: 'Git-powered timeline with visual diffs and one-click restore' },
                { icon: '🧩', title: 'Component Library', desc: 'Browse, preview, and insert UI components directly into your code' },
                { icon: '⏰', title: 'Cron Jobs', desc: 'Schedule and monitor background tasks with a visual cron editor' },
                { icon: '🏗️', title: 'CI/CD Pipeline', desc: 'GitHub Actions integration with visual workflow builder' },
                { icon: '🏢', title: 'Enterprise SSO', desc: 'Google, Okta, Azure AD — with audit logs and compliance tools' },
              ].map(f => (
                <div key={f.title} className="mini-feature-card">
                  <div className="mini-feature-icon">{f.icon}</div>
                  <div className="mini-feature-title">{f.title}</div>
                  <div className="mini-feature-desc">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── TECH STACK ── */}
        <section className="flow-section">
          <div className="flow-inner">
            <div className="section-label"><span className="label-dot" style={{ background: '#b39a48' }} /><span style={{ color: '#c99858' }}>TECH STACK</span></div>
            <h2 className="flow-heading">Built with the <span className="gradient-text">best</span> tools</h2>
            <div className="tech-badges">
              {[
                { name: 'React 18', color: '#61dafb' },
                { name: 'TypeScript', color: '#9b8d0e' },
                { name: 'CodeMirror 6', color: '#7e7448' },
                { name: 'Claude API', color: '#f5a157' },
                { name: 'Socket.io', color: '#5fd4a4' },
                { name: 'Yjs CRDT', color: '#f07070' },
                { name: 'Whisper STT', color: '#f5a157' },
                { name: 'Vite', color: '#458572' },
                { name: 'Zustand', color: '#5fd4a4' },
                { name: 'Tailwind', color: '#38bdf8' },
              ].map(t => (
                <div key={t.name} className="tech-badge" style={{ borderColor: `${t.color}33`, background: `${t.color}0a` }}>
                  <div className="tech-badge-dot" style={{ background: t.color, boxShadow: `0 0 8px ${t.color}` }} />
                  <span style={{ color: t.color }}>{t.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── COMPARISON TABLE ── */}
        <section className="flow-section">
          <div className="flow-inner">
            <div className="section-label"><span className="label-dot" style={{ background: '#f07070' }} /><span style={{ color: '#f07070' }}>WHY PULSE</span></div>
            <h2 className="flow-heading"><span className="gradient-text-alt">PULSE vs the rest</span></h2>
            <div className="table-wrap">
              <table className="compare-table">
                <thead>
                  <tr>
                    <th className="th-feature">Feature</th>
                    <th className="th-pulse">PULSE</th>
                    <th>Cursor</th>
                    <th>Bolt</th>
                    <th>Replit</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Voice coding', '✓', '✗', '✗', '✗'],
                    ['Real-time collab', '✓', '✗', '✗', '✓'],
                    ['Prompt to full app', '✓', '✗', '✓', '✓'],
                    ['Runs in browser', '✓', '✗', '✓', '✓'],
                    ['Desktop app', '✓', '✓', '✗', '✗'],
                    ['VS Code extension', '✓', '✓', '✗', '✗'],
                    ['Multi-model AI', '✓', '✓', '✗', '✗'],
                    ['One-click deploy', '✓', '✗', '✓', '✓'],
                    ['Open source', '✓', '✗', '✗', '✗'],
                  ].map(([feat, ...vals]) => (
                    <tr key={feat}>
                      <td className="td-feature">{feat}</td>
                      {vals.map((v, vi) => (
                        <td key={vi} className={v === '✓' ? (vi === 0 ? 'td-yes-pulse' : 'td-yes') : 'td-no'}>{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section className="flow-section">
          <div className="flow-inner">
            <div className="section-label"><span className="label-dot" style={{ background: '#f5a157' }} /><span style={{ color: '#f5a157' }}>TESTIMONIALS</span></div>
            <h2 className="flow-heading">Loved by <span className="gradient-text-warm">developers</span></h2>
            <div className="testimonials-grid">
              {[
                { name: 'Sarah K.', role: 'Full-stack Dev', text: 'Voice coding changed everything. I build features 3x faster now without touching the keyboard.' },
                { name: 'Marcus T.', role: 'Startup Founder', text: 'Went from idea to deployed MVP in 20 minutes. PULSE is the future of building software.' },
                { name: 'Aisha R.', role: 'Senior Engineer', text: 'The real-time collab is seamless. My team codes together like we\'re in the same room.' },
              ].map(t => (
                <div key={t.name} className="testimonial-card">
                  <p className="testimonial-text">"{t.text}"</p>
                  <div className="testimonial-author">
                    <div className="testimonial-avatar">{t.name[0]}</div>
                    <div>
                      <div className="testimonial-name">{t.name}</div>
                      <div className="testimonial-role">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── LIVE DEMO ── */}
        <section className="flow-section">
          <div className="flow-inner">
            <div className="section-label"><span className="label-dot" style={{ background: '#5fd4a4' }} /><span style={{ color: '#5fd4a4' }}>SEE IT IN ACTION</span></div>
            <h2 className="flow-heading">Watch PULSE <span className="gradient-text">build an app</span></h2>
            <div className="demo-video">
              <div className="demo-placeholder">
                <div className="demo-play">▶</div>
                <div className="demo-text">Demo video coming soon</div>
                <div className="demo-sub">Voice coding • Prompt to app • Real-time collab</div>
              </div>
            </div>
          </div>
        </section>

        {/* ── WAITLIST ── */}
        <section className="flow-section">
          <div className="flow-inner flow-narrow">
            <div className="section-label"><span className="label-dot" style={{ background: '#f5a157' }} /><span style={{ color: '#f5a157' }}>EARLY ACCESS</span></div>
            <h2 className="flow-heading">Join the <span className="gradient-text-warm">waitlist</span></h2>
            <p className="flow-subtext">Be the first to know when new features drop</p>
            <div className="waitlist-form">
              <input type="email" placeholder="you@email.com" className="waitlist-input" />
              <button className="waitlist-btn">Join Waitlist</button>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="flow-section cta-section">
          <div className="flow-inner">
            {/* PULSE complete word with glow animation */}
            <div className="letter-wrapper pulse-bottom-word">
              <div className="section-letter full-word anim-assemble visible">PULSE</div>
              <div className="section-letter-glow full-word anim-assemble visible">PULSE</div>
            </div>
            <h2 className="cta-heading">Ready to build<br /><span className="gradient-text">anything?</span></h2>
            <p className="cta-sub">Join thousands of developers building the future with PULSE</p>
            <button className="cta-btn" onClick={() => onSubmit?.('__login__')}>Get Started Free →</button>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer className="site-footer">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="footer-logo">
                <div className="footer-logo-mark" />
                <span>PULSE</span>
              </div>
              <p>The AI IDE that beats everything. Voice-native, real-time collab, runs everywhere.</p>
            </div>
            <div className="footer-col">
              <div className="footer-col-title">PRODUCT</div>
              {['Features', 'Pricing', 'Voice Coding', 'Collab', 'Deploy', 'Templates'].map(l => <div key={l} className="footer-link">{l}</div>)}
            </div>
            <div className="footer-col">
              <div className="footer-col-title">RESOURCES</div>
              {['Documentation', 'API Reference', 'Changelog', 'Blog', 'Status'].map(l => <div key={l} className="footer-link">{l}</div>)}
            </div>
            <div className="footer-col">
              <div className="footer-col-title">COMMUNITY</div>
              {['GitHub', 'Discord', 'Twitter / X', 'YouTube'].map(l => <div key={l} className="footer-link">{l}</div>)}
            </div>
            <div className="footer-col">
              <div className="footer-col-title">LEGAL</div>
              {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map(l => <div key={l} className="footer-link">{l}</div>)}
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2027 PULSE — The AI IDE that beats everything</p>
          </div>
        </footer>
      </div>
    </div>
  )
}
